import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { IconCategory, MapArrow } from '@warplan/shared';
import { getOrderedCategoriesForMode, ICON_CATALOG } from '../lib/icon-catalog.js';
import { getCategoryStroke } from '../lib/category-colors.js';

const POPOVER_W = 260;

export interface MapArrowPopoverProps {
  /** Screen-space anchor (client coordinates). */
  x: number;
  y: number;
  arrow: MapArrow;
  canEdit: boolean;
  onPatch: (patch: {
    kind?: IconCategory;
    color?: string | null;
    arrowheadStart?: boolean;
    arrowheadEnd?: boolean;
    dashed?: boolean;
    metadata?: { note?: string };
  }) => void;
  onDelete: () => void;
  onClose: () => void;
  forbiddenCategories?: ReadonlySet<IconCategory> | undefined;
}

export function MapArrowPopover({
  x,
  y,
  arrow,
  canEdit,
  onPatch,
  onDelete,
  onClose,
  forbiddenCategories,
}: MapArrowPopoverProps): React.JSX.Element {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [note, setNote] = useState(arrow.metadata.note ?? '');
  const noteRef = useRef(arrow.metadata.note ?? '');
  const noteValueRef = useRef(arrow.metadata.note ?? '');

  useEffect(() => {
    setNote(arrow.metadata.note ?? '');
    noteRef.current = arrow.metadata.note ?? '';
    noteValueRef.current = arrow.metadata.note ?? '';
  }, [arrow.id, arrow.metadata.note]);

  function commitNoteIfChanged(): void {
    if (!canEdit) return;
    const current = noteValueRef.current;
    if (current === noteRef.current) return;
    const trimmed = current.trim();
    onPatch({ metadata: trimmed ? { note: trimmed } : {} });
    noteRef.current = current;
  }

  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (rootRef.current && e.target instanceof Node && !rootRef.current.contains(e.target)) {
        commitNoteIfChanged();
        onClose();
      }
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        commitNoteIfChanged();
        onClose();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const left = Math.min(Math.max(x + 12, 8), window.innerWidth - POPOVER_W - 8);
  const top = Math.max(y + 12, 8);

  const style: CSSProperties = {
    position: 'fixed',
    left,
    top,
    width: POPOVER_W,
    zIndex: 50,
  };

  const resolvedColor = arrow.color ?? getCategoryStroke(arrow.kind);
  const colorIsCustom = arrow.color !== null;

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Edit arrow"
      style={style}
      className="rounded border border-slate-700 bg-slate-900/95 p-3 text-slate-100 shadow-2xl backdrop-blur"
    >
      <div className="mb-2 flex items-center gap-2">
        <div
          aria-hidden="true"
          style={{ background: resolvedColor }}
          className="h-4 w-6 rounded"
        />
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">
          Flèche · {arrow.points.length} pts
        </span>
        <button
          type="button"
          onClick={() => {
            commitNoteIfChanged();
            onClose();
          }}
          aria-label="Close"
          className="ml-auto rounded px-1.5 py-0.5 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
        >
          ✕
        </button>
      </div>

      <fieldset disabled={!canEdit} className="space-y-2">
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">
            Catégorie
          </div>
          <div className="flex gap-1">
            {getOrderedCategoriesForMode(arrow.mode).map((cat) => {
              const disabled = forbiddenCategories?.has(cat) ?? false;
              const isActive = arrow.kind === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  title={ICON_CATALOG[cat].label}
                  disabled={disabled}
                  aria-pressed={isActive}
                  onClick={() => onPatch({ kind: cat })}
                  className={
                    'h-7 flex-1 rounded border text-[10px] font-semibold uppercase transition ' +
                    (disabled
                      ? 'cursor-not-allowed border-slate-700 bg-slate-800 text-slate-500 opacity-40'
                      : isActive
                        ? 'border-emerald-400 ring-1 ring-emerald-300'
                        : 'border-slate-700 hover:border-slate-500')
                  }
                  style={{
                    background: isActive ? getCategoryStroke(cat) : 'transparent',
                    color: isActive ? '#fff' : getCategoryStroke(cat),
                  }}
                >
                  {ICON_CATALOG[cat].label.slice(0, 3)}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">
            Couleur
          </div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={resolvedColor}
              onChange={(e) => onPatch({ color: e.target.value })}
              className="h-7 w-12 cursor-pointer rounded border border-slate-700 bg-slate-800"
            />
            <button
              type="button"
              onClick={() => onPatch({ color: null })}
              disabled={!colorIsCustom}
              className={
                'rounded border border-slate-700 px-2 py-1 text-[11px] ' +
                (colorIsCustom
                  ? 'text-slate-200 hover:bg-slate-700'
                  : 'cursor-not-allowed text-slate-500 opacity-50')
              }
              title="Réinitialiser à la couleur de la catégorie"
            >
              Reset
            </button>
          </div>
        </div>

        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">
            Pointes
          </div>
          <div className="flex gap-1">
            {(
              [
                { key: 'none', label: '—', start: false, end: false },
                { key: 'end', label: '→', start: false, end: true },
                { key: 'start', label: '←', start: true, end: false },
                { key: 'both', label: '↔', start: true, end: true },
              ] as const
            ).map((opt) => {
              const isActive =
                arrow.arrowheadStart === opt.start && arrow.arrowheadEnd === opt.end;
              return (
                <button
                  key={opt.key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() =>
                    onPatch({ arrowheadStart: opt.start, arrowheadEnd: opt.end })
                  }
                  className={
                    'h-7 flex-1 rounded border text-sm font-semibold transition ' +
                    (isActive
                      ? 'border-emerald-400 bg-emerald-600 text-white'
                      : 'border-slate-700 text-slate-200 hover:bg-slate-700')
                  }
                  title={opt.label}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">
            Style
          </div>
          <div className="flex gap-1">
            {(
              [
                { key: 'solid', label: 'Plein', dashed: false },
                { key: 'dashed', label: 'Tirets', dashed: true },
              ] as const
            ).map((opt) => {
              const isActive = arrow.dashed === opt.dashed;
              return (
                <button
                  key={opt.key}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => onPatch({ dashed: opt.dashed })}
                  className={
                    'flex h-7 flex-1 items-center justify-center gap-1 rounded border text-[10px] font-semibold uppercase transition ' +
                    (isActive
                      ? 'border-emerald-400 bg-emerald-600 text-white'
                      : 'border-slate-700 text-slate-200 hover:bg-slate-700')
                  }
                >
                  <svg width="28" height="8" viewBox="0 0 28 8" aria-hidden="true">
                    <line
                      x1="2"
                      y1="4"
                      x2="26"
                      y2="4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray={opt.dashed ? '6 5' : undefined}
                    />
                  </svg>
                  <span>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">
            Note
          </div>
          <textarea
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              noteValueRef.current = e.target.value;
            }}
            onBlur={commitNoteIfChanged}
            rows={2}
            maxLength={2000}
            className="w-full resize-none rounded border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-slate-100 focus:border-emerald-400 focus:outline-none"
            placeholder="Annotation libre…"
          />
        </div>
      </fieldset>

      {canEdit ? (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onDelete}
            className="rounded bg-red-700 px-3 py-1 text-xs text-white hover:bg-red-600"
          >
            Supprimer
          </button>
        </div>
      ) : null}
    </div>
  );
}
